// ============================================================================
// CLEO'S SECRET — EmailGate
// File: src/pages/EmailGate.jsx
//
// First-conversion form. Single-screen, centered. Collects first name + email
// before routing the user into the intake conversation.
//
// Stage 2 + landing-template typography applied:
//   • Page opener: AnkhMed brand mark, h1 (roman + italic block-spans),
//     gold accent rule, body intro
//   • Form: PAP inputs on SAND page bg, 1px SAND3 borders (focus → BR),
//     Cinzel uppercase labels at weight 700, BtnPrimary large submit
//   • Inline validation error in italic Cormorant (not a tiny red eyebrow)
//   • Footer in Cinzel uppercase tracked + italic disclaimer
// ============================================================================

import React, { useState } from 'react';
import {
  FF, P, T,
  BtnPrimary,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

export default function EmailGate(props) {
  var onSubmit = props.onSubmit;

  var emailState = useState("");
  var email = emailState[0];
  var setEmail = emailState[1];

  var firstNameState = useState("");
  var firstName = firstNameState[0];
  var setFirstName = firstNameState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  function handleSubmit(e) {
    if (e) e.preventDefault();
    setError(null);

    var cleanEmail = email.trim().toLowerCase();
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (firstName.trim().length < 1) {
      setError("Please enter your first name.");
      return;
    }
    if (!emailRegex.test(cleanEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    onSubmit({
      email: cleanEmail,
      firstName: firstName.trim()
    });
  }

  // ───────── Input style (shared for both fields) ─────────
  var inputBase = {
    width: '100%',
    padding: 'clamp(14px, 1.4vw, 18px) clamp(16px, 1.6vw, 22px)',
    fontFamily: FF,
    fontSize: 'clamp(16px, 1.25vw, 18px)',
    fontWeight: 400,
    background: P.PAP,
    color: P.INK,
    border: '1px solid ' + P.SAND3,
    borderRadius: 0,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };
  var onInputFocus = function(e) { e.target.style.borderColor = P.BR; };
  var onInputBlur  = function(e) { e.target.style.borderColor = P.SAND3; };

  // ───────── Label style ─────────
  var labelStyle = {
    display: 'block',
    fontFamily: FF,
    fontSize: 'clamp(11px, 0.9vw, 13px)',
    fontWeight: 700,
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: P.BR2,
    marginBottom: 10,
  };

  return (
    <div style={{
      fontFamily: FF,
      background: P.SAND,
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(40px, 6vw, 80px) clamp(20px, 4vw, 48px)',
    }}>
      <div style={{
        maxWidth: 640,
        width: '100%',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {/* Brand mark — ceremonial single-anchor ankh, top of column */}
        <div style={{ marginBottom: 'clamp(24px, 2.5vw, 36px)' }}>
          <Ankh responsive="opener" color={P.GO} />
        </div>

        {/* Title — h1 roman + italic block-spans */}
        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(36px, 4.8vw, 60px)',
          fontWeight: 400,
          lineHeight: 1.08,
          letterSpacing: '-0.018em',
          color: P.INK,
          margin: '0 0 32px',
          maxWidth: 600,
        }}>
          <span style={{ display: 'block' }}>Begin your conversation</span>
          <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>with Cleo.</span>
        </h1>

        {/* Gold accent rule */}
        <div style={{
          width: 'clamp(140px, 18vw, 220px)',
          height: 2,
          background: P.GO,
          opacity: 0.9,
          margin: '0 0 clamp(28px, 3vw, 40px)',
        }} />

        {/* Body intro */}
        <p style={{
          ...T.bodyLg,
          color: P.TX,
          maxWidth: 460,
          margin: '0 0 clamp(36px, 4vw, 56px)',
        }}>
          Your name and email. Your GP will read what we discuss before you meet in person.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'clamp(18px, 1.8vw, 24px)',
          textAlign: 'left',
        }}>
          {/* First name */}
          <div>
            <label htmlFor="cs-firstname" style={labelStyle}>First name</label>
            <input
              id="cs-firstname"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={function(e) { setFirstName(e.target.value); }}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              placeholder="Your first name"
              style={inputBase}
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="cs-email" style={labelStyle}>Email address</label>
            <input
              id="cs-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={function(e) { setEmail(e.target.value); }}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              placeholder="you@example.com"
              style={inputBase}
            />
          </div>

          {/* Inline validation error — italic Cormorant, never a tiny red eyebrow */}
          {error && (
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.15vw, 17px)',
              fontStyle: 'italic',
              color: P.RED,
              padding: '4px 2px',
            }}>
              {error}
            </div>
          )}

          {/* Submit */}
          <div style={{ marginTop: 'clamp(8px, 1vw, 16px)', textAlign: 'center' }}>
            <BtnPrimary onClick={handleSubmit} large>Start your conversation</BtnPrimary>
          </div>
        </form>

        {/* Footer note */}
        <div style={{ marginTop: 'clamp(36px, 4vw, 56px)' }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(10px, 0.85vw, 12px)',
            fontWeight: 700,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 10,
          }}>
            Private and confidential &middot; Reviewed by your clinical team
          </div>
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(13px, 1vw, 15px)',
            fontStyle: 'italic',
            color: P.BR2,
            margin: 0,
          }}>
            Not a medical consultation.
          </p>
        </div>
      </div>
    </div>
  );
}
